import React, { useState, useEffect, useRef } from 'react';
import { latLngToCell } from 'h3-js';
import { MapPin, Search, CheckCircle, Navigation } from 'lucide-react';
import { getCurrentPosition, reverseGeocode } from '../utils/location';

const BRAND_COLORS = {
  primary: '#1a1a1a',
  accent: '#C9A961',
  light: '#F5F5F0',
  lightGray: '#E8E8E3',
};

const MapLocationPicker = ({ location, setLocation, setError }) => {
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 }); // India center
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    // Wait for Google Maps to load
    const initMapWhenReady = () => {
      if (window.google && window.google.maps) {
        initMap();
      } else {
        setTimeout(initMapWhenReady, 100);
      }
    };

    initMapWhenReady();
  }, []);

  const initMap = () => {
    if (!mapRef.current || !window.google) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: mapCenter,
      zoom: 15, // Zoom in by default
    });

    mapInstanceRef.current = map;

    // Add click listener to map
    map.addListener('click', (e) => {
      handleMapClick(e.latLng);
    });

    // If location already set, place marker
    if (location) {
      placeMarker({ lat: location.lat, lng: location.lng }, map);
      map.setCenter({ lat: location.lat, lng: location.lng });
      map.setZoom(15);
    } else {
      // Auto-detect location if not set
      handleUseCurrentLocation();
    }
  };

  const placeMarker = async (latLng, map) => {
    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Create new marker
    const marker = new window.google.maps.Marker({
      position: latLng,
      map: map,
      animation: window.google.maps.Animation.DROP,
    });

    markerRef.current = marker;

    // Get address
    try {
      const addressFormatted = await reverseGeocode(latLng.lat, latLng.lng);
      const h3Index = latLngToCell(latLng.lat, latLng.lng, 8);
      setLocation({
        lat: latLng.lat,
        lng: latLng.lng,
        addressFormatted,
        h3Index,
      });
    } catch (err) {
      setError('Failed to get address for this location');
    }
  };

  const handleMapClick = (latLng) => {
    placeMarker({ lat: latLng.lat(), lng: latLng.lng() }, mapInstanceRef.current);
  };

  const handleUseCurrentLocation = async () => {
    setLoading(true);
    setError('');

    try {
      const position = await getCurrentPosition();
      const latLng = { lat: position.lat, lng: position.lng };

      if (mapInstanceRef.current) {
        mapInstanceRef.current.setCenter(latLng);
        mapInstanceRef.current.setZoom(15);
        placeMarker(latLng, mapInstanceRef.current);
      }
    } catch (err) {
      // Silently fail or show error if user explicitly clicked button (but button is gone now?)
      // If this is auto-called, we might not want to show a big error immediately,
      // but the original code set error. Let's keep it for now.
      console.error("Failed to get location", err);
      // setError('Failed to get current location');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Location Button */}
      <button
        type="button"
        onClick={handleUseCurrentLocation}
        disabled={loading}
        className="w-full py-3 px-4 border-2 bg-transparent font-light tracking-wide transition-all duration-300 hover:border-black flex items-center justify-center space-x-2"
        style={{ borderColor: BRAND_COLORS.lightGray }}
      >
        <Navigation className="w-5 h-5" />
        <span>{loading ? 'GETTING LOCATION...' : 'USE CURRENT LOCATION'}</span>
      </button>

      {/* Map Container */}
      <div
        ref={mapRef}
        className="w-full h-96 border-2"
        style={{ borderColor: BRAND_COLORS.lightGray }}
      />

      <p className="text-xs font-light text-gray-500 text-center">
        Click on the map to set your business location
      </p>

      {/* Selected Location Display */}
      {location && (
        <div className="p-4 border-2 bg-green-50" style={{ borderColor: BRAND_COLORS.accent }}>
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-6 h-6 flex-shrink-0" style={{ color: BRAND_COLORS.accent }} />
            <div className="flex-1">
              <p className="font-light mb-1" style={{ color: BRAND_COLORS.primary }}>
                Location Selected
              </p>
              <p className="text-sm font-light text-gray-600">
                {location.addressFormatted}
              </p>
              <p className="text-xs font-light text-gray-500 mt-1">
                Lat: {location.lat.toFixed(6)}, Lng: {location.lng.toFixed(6)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapLocationPicker;
